import { z } from 'zod';
import { MODULOS, PAQUETES_COMERCIALES } from '../catalogos/modulos.js';
import { esquemaId, esquemaMotivo } from './comunes.js';
/**
 * Estado comercial y estado operativo, por separado (plan maestro §11.5).
 *
 * No se reutiliza `Organizacion.activa` para la mora: el middleware de
 * autenticacion rechaza toda sesion de una organizacion inactiva
 * (`auth.middleware.ts`), y un consultorio que debe una mensualidad tiene que
 * poder leer y exportar sus expedientes.
 */
export const ESTADOS_COMERCIALES = [
    'prueba',
    'activa',
    'pago-pendiente',
    'restringida',
    'cancelacion-programada',
    'terminada',
];
/**
 * Estado de operacion de la organizacion.
 *
 * `suspendida-seguridad` es distinto de cualquier estado comercial: revoca
 * sesiones de inmediato ante una cuenta comprometida, sin importar si paga.
 */
export const ESTADOS_OPERATIVOS = ['aprovisionando', 'activa', 'suspendida-seguridad'];
/**
 * Categorias de accion que el estado comercial puede limitar.
 *
 * - `lectura`: consultar lo existente.
 * - `exportacion`: sacar el expediente completo.
 * - `escritura-clinica`: crear y firmar documentos.
 * - `consumo`: lo que gasta recursos medidos (subir archivos, enviar avisos).
 * - `administracion`: usuarios, marca, configuracion.
 */
export const CATEGORIAS_DE_ACCION = [
    'lectura',
    'exportacion',
    'escritura-clinica',
    'consumo',
    'administracion',
];
/**
 * Que permite cada estado comercial.
 *
 * Lectura y exportacion nunca se cortan por facturacion: el consultorio es
 * responsable del expediente y tiene que poder sacarlo aunque deje de pagar
 * (plan maestro §11.5, §14.7). Es un punto de partida para revision del dueno,
 * no una politica contractual.
 */
export const ACCIONES_POR_ESTADO_COMERCIAL = {
    prueba: CATEGORIAS_DE_ACCION,
    activa: CATEGORIAS_DE_ACCION,
    'pago-pendiente': CATEGORIAS_DE_ACCION,
    restringida: ['lectura', 'exportacion', 'escritura-clinica', 'administracion'],
    'cancelacion-programada': CATEGORIAS_DE_ACCION,
    terminada: ['lectura', 'exportacion'],
};
/** Origen de una capacidad: de donde viene que un modulo este habilitado. */
export const ORIGENES_DE_ENTITLEMENT = ['plan', 'addon', 'concesion', 'compatibilidad'];
const esquemaFechaHora = z.coerce.date();
/**
 * Un entitlement: un modulo habilitado para una organizacion, con su origen y
 * vigencia. Una concesion manual exige motivo (plan maestro §6.6).
 */
export const esquemaEntitlement = z
    .object({
    modulo: z.enum(MODULOS),
    origen: z.enum(ORIGENES_DE_ENTITLEMENT),
    motivo: z.string().trim().max(300).default(''),
    vigenteDesde: esquemaFechaHora,
    vigenteHasta: esquemaFechaHora.nullable().default(null),
})
    .refine((e) => e.origen !== 'concesion' || e.motivo.length >= 5, {
    message: 'Una concesión manual necesita un motivo de al menos 5 caracteres',
    path: ['motivo'],
})
    .refine((e) => e.vigenteHasta === null || e.vigenteHasta > e.vigenteDesde, {
    message: 'La vigencia termina antes de empezar',
    path: ['vigenteHasta'],
});
export const esquemaSuscripcion = z.object({
    paquete: z.enum(PAQUETES_COMERCIALES),
    estadoComercial: z.enum(ESTADOS_COMERCIALES),
    inicio: esquemaFechaHora,
    finDePeriodo: esquemaFechaHora.nullable().default(null),
    /** Fin de la gracia en mora; `null` si no hay mora. */
    graciaHasta: esquemaFechaHora.nullable().default(null),
});
/** Si un estado comercial permite una categoria de accion. */
export function estadoPermite(estado, categoria) {
    return ACCIONES_POR_ESTADO_COMERCIAL[estado].includes(categoria);
}
// --- F5.1: catalogo comercial, suscripcion y su efecto en la politica ----------
/**
 * Categoria de cada permiso, para que el estado comercial decida (§11.5).
 *
 * - `consumo`: lo que gasta recursos medidos: subir archivos y compartir o
 *   publicar al portal (dispara avisos).
 * - `escritura-clinica`: crear y firmar lo clinico. Se mantiene en
 *   `restringida`: cortar la escritura a media atencion seria un riesgo
 *   clinico, no una medida de cobro.
 * - `administracion`: usuarios, marca, agenda y plantillas.
 *
 * Todo permiso tiene categoria: el `Record` obliga a decidir al agregar uno.
 */
export const CATEGORIA_DE_PERMISO = {
    'organizacion:leer': 'lectura',
    'organizacion:editar': 'administracion',
    'usuarios:leer': 'lectura',
    'usuarios:administrar': 'administracion',
    'pacientes:leer': 'lectura',
    'pacientes:crear': 'escritura-clinica',
    'pacientes:editar': 'escritura-clinica',
    'pacientes:eliminar': 'escritura-clinica',
    'pacientes:remitir': 'escritura-clinica',
    'credenciales:revisar': 'administracion',
    'historia:leer': 'lectura',
    'historia:escribir': 'escritura-clinica',
    'encuentros:leer': 'lectura',
    'encuentros:escribir': 'escritura-clinica',
    'observaciones:leer': 'lectura',
    'observaciones:escribir': 'escritura-clinica',
    'antecedentes:leer': 'lectura',
    'antecedentes:escribir': 'escritura-clinica',
    'colposcopias:leer': 'lectura',
    'colposcopias:crear': 'escritura-clinica',
    'colposcopias:editar': 'escritura-clinica',
    'citas:leer': 'lectura',
    'citas:administrar': 'escritura-clinica',
    'agenda:configurar': 'administracion',
    'recetas:leer': 'lectura',
    'recetas:emitir': 'escritura-clinica',
    'notas:leer': 'lectura',
    'notas:escribir': 'escritura-clinica',
    'plantillas:leer': 'lectura',
    'plantillas:administrar': 'administracion',
    'documentos:leer': 'lectura',
    'documentos:escribir': 'consumo',
    'documentos:compartir': 'consumo',
    'estudios:leer': 'lectura',
    'estudios:escribir': 'escritura-clinica',
    'estudios:publicar': 'consumo',
    'planes:leer': 'lectura',
    'planes:escribir': 'escritura-clinica',
    'seguimiento:leer': 'lectura',
    'seguimiento:revisar': 'escritura-clinica',
    'mensajes:clinicos': 'escritura-clinica',
    'mensajes:administrativos': 'escritura-clinica',
    'documentos:supervisar': 'escritura-clinica',
    'auditoria:leer': 'lectura',
    'expediente:exportar': 'exportacion',
};
/**
 * El estado que manda HOY, a partir del guardado y las fechas. Una gracia
 * vencida restringe; una prueba vencida, tambien; una cancelacion programada
 * termina al cerrar su periodo. Nada de esto borra datos.
 */
export function estadoComercialEfectivo(suscripcion, ahora = new Date()) {
    // Sin suscripcion: consultorio anterior a F5.1 que aun no migra. Se trata
    // como activo; la migracion 003 le crea la de compatibilidad.
    if (!suscripcion)
        return 'activa';
    const antes = (fecha) => fecha !== null && new Date(fecha) <= ahora;
    switch (suscripcion.estadoComercial) {
        case 'pago-pendiente':
            return antes(suscripcion.graciaHasta) ? 'restringida' : 'pago-pendiente';
        case 'prueba':
            return antes(suscripcion.finDePeriodo) ? 'restringida' : 'prueba';
        case 'cancelacion-programada':
            return antes(suscripcion.finDePeriodo) ? 'terminada' : 'cancelacion-programada';
        default:
            return suscripcion.estadoComercial;
    }
}
export const PERIODICIDADES = ['mensual', 'anual'];
/**
 * Un plan comercial es un catalogo global y VERSIONADO: cambiar el precio crea
 * una version nueva y no toca las suscripciones que aceptaron la anterior
 * (§11.3). Sin precios definidos todavia: `precioCentavos` puede ser nulo.
 */
export const esquemaCrearPlanComercial = z
    .object({
    clave: z
        .string()
        .trim()
        .regex(/^[a-z0-9-]{3,40}$/, 'Minúsculas, números y guiones'),
    nombre: z.string().trim().min(3).max(80),
    paquete: z.enum(PAQUETES_COMERCIALES),
    periodicidad: z.enum(PERIODICIDADES),
    precioCentavos: z.number().int().min(0).nullable().default(null),
    moneda: z
        .string()
        .trim()
        .regex(/^[A-Z]{3}$/)
        .default('MXN'),
})
    .strict();
export const esquemaAsignarSuscripcion = z
    .object({
    planComercialId: esquemaId,
    estado: z.enum(['prueba', 'activa']),
    /** Obligatorio en prueba: una prueba sin fin no es una prueba. */
    finDePeriodo: z.coerce.date().optional(),
    motivo: esquemaMotivo,
})
    .strict()
    .refine((d) => d.estado !== 'prueba' || d.finDePeriodo !== undefined, {
    message: 'Una prueba necesita fecha de fin',
    path: ['finDePeriodo'],
});
export const esquemaCambiarEstadoComercial = z
    .object({
    estado: z.enum(ESTADOS_COMERCIALES),
    motivo: esquemaMotivo,
    /** En `pago-pendiente`: hasta cuando dura la gracia. */
    graciaHasta: z.coerce.date().optional(),
    /** En `cancelacion-programada` o `prueba`: cuando termina. */
    finDePeriodo: z.coerce.date().optional(),
})
    .strict()
    .refine((d) => d.estado !== 'pago-pendiente' || d.graciaHasta !== undefined, {
    message: 'El pago pendiente necesita el fin de la gracia',
    path: ['graciaHasta'],
})
    .refine((d) => !['cancelacion-programada', 'prueba'].includes(d.estado) || d.finDePeriodo !== undefined, { message: 'Ese estado necesita fecha de fin', path: ['finDePeriodo'] });
export const TIPOS_DE_ADDON = ['modulo', 'usuarios', 'almacenamiento'];
export const esquemaAgregarAddon = z
    .object({
    tipo: z.enum(TIPOS_DE_ADDON),
    modulo: z.enum(MODULOS).optional(),
    /** Usuarios o bytes adicionales; en un modulo no aplica. */
    cantidad: z.number().int().min(1).optional(),
    motivo: esquemaMotivo,
})
    .strict()
    .refine((a) => (a.tipo === 'modulo') === (a.modulo !== undefined), {
    message: 'Un add-on de módulo lleva el módulo, y solo él',
    path: ['modulo'],
})
    .refine((a) => a.tipo === 'modulo' || a.cantidad !== undefined, {
    message: 'Indique la cantidad',
    path: ['cantidad'],
});
export const esquemaCuentaFacturacion = z
    .object({
    razonSocial: z.string().trim().min(2).max(200),
    identificadorFiscal: z.string().trim().max(30).default(''),
    correo: z.string().trim().email().max(200),
    direccion: z.string().trim().max(500).default(''),
})
    .strict();
//# sourceMappingURL=suscripcion.js.map